/**
 * Esquema de validación estricta del documento de proyecto (Zod) para guardar e importar
 * (ADR 0008, doc. 03 §4 y §6). Refleja los tipos de `src/nucleo/dominio`.
 */

import { z } from 'zod';
import type { Distribucion } from '../nucleo/aleatoriedad/distribuciones';
import type { Proyecto } from '../nucleo/dominio/espacio';
import type { Condicion } from '../nucleo/dominio/proceso';

// Sin compilación con `new Function`: la política de seguridad del sitio no permite evaluar código
// (public/_headers) y la sonda de Zod generaría reportes de violación en la consola.
z.config({ jitless: true });

const numero = z.number().finite();
const origen = z.enum(['ingresado', 'supuesto', 'importado', 'calibrado']);
const parametro = <T extends z.ZodType>(valor: T) =>
  z.object({
    valor,
    unidad: z.string(),
    origen,
    nota: z.string().optional(),
    supuesto: valor.optional(),
  });
const hora = z.string().regex(/^\d{1,2}:\d{2}$/, 'Hora con formato hh:mm');

const distribucion: z.ZodType<Distribucion> = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('constante'), valor: numero }),
  z.object({ tipo: z.literal('triangular'), min: numero, tipico: numero, max: numero }),
  z.object({ tipo: z.literal('uniforme'), min: numero, max: numero }),
  z.object({ tipo: z.literal('exponencial'), media: numero, truncarEn: numero.optional() }),
  z.object({
    tipo: z.literal('lognormal'),
    media: numero,
    cv: numero,
    truncarEn: numero.optional(),
  }),
  z.object({
    tipo: z.literal('lognormal-mediana'),
    mediana: numero,
    p95: numero,
    truncarEn: numero.optional(),
  }),
]);

const tipoEstacion = z.enum([
  'bahia',
  'recepcion',
  'patio',
  'lavado',
  'entrega',
  'ingreso',
  'salida',
]);
const rol = z.enum(['asesor', 'tecnico']);
const hito = z.enum(['apertura-ot', 'trabajo-terminado', 'listo', 'fin']);
const categoria = z.enum([
  'valor-agregado',
  'administrativa',
  'traslado',
  'espera',
  'espera-cliente',
]);

const condicion: z.ZodType<Condicion> = z.lazy(() =>
  z.union([
    z.object({ tipoUnidad: z.string() }),
    z.object({ atributo: z.string(), igual: z.boolean() }),
    z.object({ probabilidad: numero.min(0).max(1) }),
    z.object({ ubicacionActual: tipoEstacion }),
    z.object({ y: z.array(condicion) }),
    z.object({ o: z.array(condicion) }),
    z.object({ no: condicion }),
  ]),
);

const etapa = z.object({
  id: z.string().min(1),
  nombre: z.string(),
  tipo: z.enum(['actividad', 'espera-externa', 'salida']),
  categoria,
  obligatoria: z.boolean(),
  aplicaA: z.array(z.string()).optional(),
  lugar: z.union([tipoEstacion, z.literal('actual')]),
  lugarDeEspera: z.enum(['actual', 'patio']).optional(),
  personas: z.array(
    z.object({ rol, retenerHasta: z.string().optional(), deLaUnidad: z.boolean().optional() }),
  ),
  duracion: z
    .object({
      porTipo: z.record(z.string(), distribucion),
      origen,
      nota: z.string().optional(),
      supuesto: z.record(z.string(), distribucion).optional(),
    })
    .optional(),
  ventanaHabil: z.object({ inicio: hora, fin: hora }).optional(),
  interrupcion: z.object({ si: condicion, fraccion: distribucion, etapa: z.string() }).optional(),
  hitoAlIniciar: hito.optional(),
  hitoAlTerminar: hito.optional(),
});

const proceso = z.object({
  id: z.string(),
  nombre: z.string(),
  version: z.number().int(),
  tiposDeUnidad: z.array(
    z.object({
      id: z.string().min(1),
      nombre: z.string(),
      promesaMin: parametro(numero),
      promesaTipo: z.enum(['habiles', 'hoy', 'manana']).optional(),
      promesaHora: parametro(hora).optional(),
      horasVendidas: parametro(numero),
      bahias: z.array(z.enum(['mixto', 'fast-lane', 'mayor'])),
    }),
  ),
  atributos: z.array(
    z.object({
      id: z.string(),
      nombre: z.string(),
      probabilidad: parametro(z.record(z.string(), numero)),
    }),
  ),
  etapaInicial: z.string(),
  etapas: z.array(etapa).min(1),
  transiciones: z.array(
    z.object({
      de: z.string(),
      a: z.string(),
      si: condicion.optional(),
      sino: z.boolean().optional(),
      maximo: z.number().int().optional(),
    }),
  ),
});

const punto = z.object({ x: numero, z: numero });

const elemento = z.object({
  id: z.string().min(1),
  catalogoId: z.string().min(1),
  posicion: punto,
  rotacion: numero,
  dimensiones: z.object({
    ancho: numero.positive(),
    fondo: numero.positive(),
    alto: numero.nonnegative(),
  }),
  etiqueta: z.string().optional(),
  nombre: z.string().optional(),
  propiedades: z
    .object({ tipoBahia: z.enum(['mixto', 'fast-lane', 'mayor', 'inactivo']).optional() })
    .optional(),
});

const via = z.object({
  id: z.string().min(1),
  tipo: z.enum(['vehicular-doble', 'vehicular-un-sentido', 'peatonal']),
  puntos: z.array(punto).min(2, 'Una vía necesita al menos 2 puntos'),
  ancho: numero.positive(),
});

const piso = z.object({
  id: z.string().min(1),
  acabado: z.enum([
    'epoxico',
    'concreto',
    'asfalto',
    'calzada',
    'vereda',
    'cesped',
    'ceramico',
    'recepcion',
    'lavado',
    'entrega',
    'seguridad',
    'cebra',
  ]),
  puntos: z.array(punto).min(3, 'Un piso necesita al menos 3 puntos'),
});

const muro = z.object({
  id: z.string().min(1),
  puntos: z.array(punto).min(2, 'Un muro necesita al menos 2 puntos'),
  espesor: numero.positive(),
  alto: numero.positive(),
});

const modelo = z.object({
  proceso,
  dotacion: z.object({ asesor: parametro(numero), tecnico: parametro(numero) }),
  demanda: z.object({
    modo: z.enum(['perfil', 'agenda']),
    unidadesPorDia: parametro(numero),
    perfilHorario: parametro(z.array(numero)),
    mezcla: parametro(z.record(z.string(), numero)),
    agenda: z
      .array(
        z.object({
          dia: z.number().int().min(1),
          hora,
          tipoUnidad: z.string(),
          atributos: z.record(z.string(), z.boolean()).optional(),
        }),
      )
      .optional(),
  }),
  jornada: z.object({
    inicioTurno: parametro(hora),
    finTurno: parametro(hora),
    inicioCitas: parametro(hora),
    ultimaCita: parametro(hora),
    cierre: parametro(hora),
  }),
  atencion: z.object({ inicio: parametro(hora), fin: parametro(hora) }),
  redondeoPromesaMin: parametro(numero),
  movimiento: z.object({
    modo: z.enum(['distancia', 'fijo']),
    velocidadVehiculoKmh: parametro(numero),
    maniobraS: parametro(numero),
    trasladoFijoMin: parametro(numero),
  }),
  politicas: z.object({
    seleccionEstacion: z.enum(['mas-cercana', 'menor-codigo']),
    seleccionPersona: z.enum(['menor-carga', 'menor-codigo']),
    lavadoDespuesDeHora: z.boolean(),
    ordenColas: z.enum(['llegada', 'promesa']).optional(),
  }),
  refrigerio: z
    .object({
      inicio: parametro(hora),
      duracionMin: parametro(numero),
      modo: parametro(z.enum(['todos', 'escalonado'])),
    })
    .optional(),
  tecnicoPorElevador: parametro(z.enum(['libre', 'fijo'])).optional(),
});

const espacio = z.object({
  id: z.string().min(1),
  nombre: z.string(),
  marco: z.object({ x: numero, z: numero, ancho: numero.positive(), fondo: numero.positive() }),
  medidas: z
    .object({
      origen: z.enum(['ingresado', 'supuesto']),
      fuente: z.enum(['perfil', 'propuesta']).optional(),
      pedidas: z
        .object({
          ancho: numero.positive(),
          fondo: numero.positive(),
          origen: z.enum(['ingresado', 'supuesto']),
        })
        .optional(),
    })
    .optional(),
  layout: z.object({
    elementos: z.array(elemento),
    vias: z.array(via),
    pisos: z.array(piso),
    muros: z.array(muro),
    versionLayout: z.number().int().nonnegative(),
  }),
  modelo,
  configuracionAleatoria: z.object({
    semilla: z.number().int().nonnegative(),
    replicas: z.number().int().min(1).max(500),
    replicasAutomaticas: z.boolean(),
    dias: z.number().int().min(1).max(60),
  }),
  semaforo: z
    .object({
      activo: z.boolean(),
      estados: z
        .array(
          z.object({
            id: z.string().min(1),
            nombre: z.string(),
            color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Color con formato #rrggbb'),
            parpadea: z.boolean(),
            regla: z.enum(['atraso', 'listo']).optional(),
          }),
        )
        .min(1),
      asignacion: z.record(
        z.string(),
        z.object({
          espera: z.string().nullable().optional(),
          curso: z.string().nullable().optional(),
          interrumpida: z.string().nullable().optional(),
        }),
      ),
    })
    .optional(),
});

export const esquemaProyecto = z
  .object({
    versionEsquema: z.number().int().positive(),
    id: z.string().min(1),
    espacioDeTrabajoId: z.string().min(1),
    carpetaId: z.string().min(1).optional(),
    nombre: z.string(),
    descripcion: z.string(),
    origen: z
      .string()
      .regex(/^(plantilla:taller-base|lienzo|asistente|importado|duplicado|caso:[A-Za-z0-9-]+)$/),
    creadoEn: z.string(),
    actualizadoEn: z.string(),
    espacios: z.array(espacio).min(1, 'El proyecto necesita al menos un espacio'),
  })
  .superRefine((p, ctx) => {
    // Regla de integridad 1: identificadores únicos dentro del proyecto (doc. 03 §4).
    const vistos = new Set<string>();
    const revisar = (id: string, ruta: (string | number)[]) => {
      if (vistos.has(id))
        ctx.addIssue({ code: 'custom', message: `Identificador repetido: ${id}`, path: ruta });
      vistos.add(id);
    };
    p.espacios.forEach((e, i) => {
      revisar(e.id, ['espacios', i, 'id']);
      e.layout.elementos.forEach((x, j) =>
        revisar(x.id, ['espacios', i, 'layout', 'elementos', j, 'id']),
      );
      e.layout.vias.forEach((x, j) => revisar(x.id, ['espacios', i, 'layout', 'vias', j, 'id']));
      e.layout.pisos.forEach((x, j) => revisar(x.id, ['espacios', i, 'layout', 'pisos', j, 'id']));
      e.layout.muros.forEach((x, j) => revisar(x.id, ['espacios', i, 'layout', 'muros', j, 'id']));
    });
  });

/**
 * Comprobación en compilación: todo `Proyecto` del dominio cumple el esquema. Si alguien agrega un
 * campo al dominio y no al esquema, la compilación falla aquí.
 */
type ProyectoValidado = z.infer<typeof esquemaProyecto>;
export type ComprobacionEsquema = Proyecto extends ProyectoValidado ? true : never;

export const FORMATO_ARCHIVO = 'gemba-studio/proyecto';

export const esquemaArchivo = z.object({
  formato: z.literal(FORMATO_ARCHIVO),
  versionEsquema: z.number().int().positive(),
  exportadoEn: z.string(),
  versionApp: z.string(),
  contieneDatosReales: z.boolean(),
  proyecto: z.unknown(),
  ejecuciones: z.array(z.unknown()),
});

// ─── Organización (paso 1.5): espacios de trabajo y carpetas ──────────────────────────────────

export const esquemaEspacioDeTrabajo = z.object({
  versionEsquema: z.number().int().positive(),
  id: z.string().min(1),
  nombre: z.string().min(1),
  tipo: z.enum(['propio', 'cliente']),
  contieneDatosReales: z.boolean(),
  creadoEn: z.string(),
  actualizadoEn: z.string(),
});

export const esquemaCarpeta = z.object({
  versionEsquema: z.number().int().positive(),
  id: z.string().min(1),
  espacioDeTrabajoId: z.string().min(1),
  nombre: z.string().min(1),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  creadoEn: z.string(),
});
